import { useMemo, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Column } from 'primereact/column'
import {
  AlertTriangle, Plus, Eye, Siren, Shield, CheckCircle2,
  Activity,
} from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { CreateIncidentDrawer } from '../../components/platform/CreateIncidentDrawer.js'
import { useIncidents, type Incident } from '../../hooks/useIncidents.js'
import { formatDateTime } from '../../utils/formatDate.js'
import DataTable from '../../components/ui/DataTable.js'

const STATUS_OPTIONS = [
  { label: 'All', value: null },
  { label: 'OPEN', value: 'OPEN' },
  { label: 'INVESTIGATING', value: 'INVESTIGATING' },
  { label: 'RESOLVED', value: 'RESOLVED' },
  { label: 'CLOSED', value: 'CLOSED' },
]

const SEVERITY_OPTIONS = [
  { label: 'All', value: null },
  { label: 'LOW', value: 'LOW' },
  { label: 'MEDIUM', value: 'MEDIUM' },
  { label: 'HIGH', value: 'HIGH' },
  { label: 'CRITICAL', value: 'CRITICAL' },
]

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Siren
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

/* ─── Severity Badge ─── */
function SeverityBadge({ s }: { s: Incident['severity'] }) {
  const styles = {
    CRITICAL:  'bg-red-50 text-red-700',
    HIGH:      'bg-amber-50 text-amber-700',
    MEDIUM:    'bg-blue-50 text-blue-700',
    LOW:       'bg-gray-100 text-gray-500',
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${styles[s] || styles.LOW}`}>
      {s === 'CRITICAL' && <AlertTriangle className="h-3 w-3" />}
      {s}
    </span>
  )
}

/* ─── Status Badge ─── */
function StatusBadge({ s }: { s: Incident['status'] }) {
  const styles = {
    OPEN:         'bg-amber-50 text-amber-700',
    INVESTIGATING:'bg-blue-50 text-blue-700',
    RESOLVED:     'bg-emerald-50 text-emerald-700',
    CLOSED:       'bg-gray-100 text-gray-500',
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${styles[s] || styles.CLOSED}`}>
      {s}
    </span>
  )
}

export default function IncidentsPage(): JSX.Element {
  return (
    <RequirePermission permission="platform:incidents_admin">
      <IncidentsPageInner />
    </RequirePermission>
  )
}

function IncidentsPageInner(): JSX.Element {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const filters = useMemo(() => ({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(severityFilter ? { severity: severityFilter } : {}),
  }), [statusFilter, severityFilter])

  const { items, loading, refetch } = useIncidents(filters)

  function handleDrawerClose(): void {
    setDrawerOpen(false)
    void refetch()
  }

  const openCount = items.filter(i => i.status === 'OPEN').length
  const investigatingCount = items.filter(i => i.status === 'INVESTIGATING').length
  const resolvedCount = items.filter(i => i.status === 'RESOLVED').length
  const criticalCount = items.filter(i => i.severity === 'CRITICAL').length

  return (
    <div className="w-full space-y-5 px-6 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
            <Siren className="h-5 w-5 text-[#3454cf]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Incidents</h1>
            <p className="text-sm text-gray-400 mt-0.5">Track detection, response, and resolution of platform incidents.</p>
          </div>
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
        >
          <Plus className="h-4 w-4" />
          New Incident
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Open" value={openCount} icon={AlertTriangle} accent="#ef4444" />
        <StatCard label="Investigating" value={investigatingCount} icon={Activity} accent="#3b82f6" />
        <StatCard label="Resolved" value={resolvedCount} icon={CheckCircle2} accent="#10b981" />
        <StatCard label="Critical" value={criticalCount} icon={Shield} accent="#f6931c" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
            <div className="flex gap-1">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-all border ${
                    statusFilter === opt.value
                      ? 'bg-[#3454cf] text-white border-[#3454cf]'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-[#3454cf]/30 hover:text-[#3454cf]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</label>
            <div className="flex gap-1">
              {SEVERITY_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setSeverityFilter(opt.value)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-all border ${
                    severityFilter === opt.value
                      ? 'bg-[#3454cf] text-white border-[#3454cf]'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-[#3454cf]/30 hover:text-[#3454cf]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DataTable
        value={items}
        loading={loading}
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
        emptyMessage="No incidents match the current filters."
      >
        <Column
          header="Detected"
          body={(i: Incident) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(i.detectedAt)}</span>}
        />
        <Column
          header="Severity"
          body={(i: Incident) => <SeverityBadge s={i.severity} />}
        />
        <Column
          header="Title"
          body={(i: Incident) => <span className="text-sm font-semibold text-gray-800">{i.title}</span>}
        />
        <Column
          header="Status"
          body={(i: Incident) => <StatusBadge s={i.status} />}
        />
        <Column
          header="Resolved"
          body={(i: Incident) => <span className="text-xs text-gray-400">{i.resolvedAt ? formatDateTime(i.resolvedAt) : '—'}</span>}
        />
        <Column
          header="Actions"
          body={(i: Incident) => (
            <button
              onClick={() => navigate(`/platform/incidents/${i.id}`)}
              className="inline-flex items-center gap-1.5 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-sm transition-all"
              aria-label={`View ${i.title}`}
            >
              <Eye className="h-3 w-3" /> View
            </button>
          )}
          style={{ width: '80px' }}
        />
      </DataTable>

      <CreateIncidentDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
      />
    </div>
  )
}
